import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Edit, Refresh, Search, Clear, Visibility, ChevronLeft, ChevronRight, Receipt, Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchTransactions, updateTransaction, setFilters, clearError } from '../../store/slices/transactionSlice';

// These will be defined inside the component to use translations

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return '#D97706'; // Dark yellow/amber
    case 'processing':
      return '#0EA5E9'; // Sky blue
    case 'completed':
      return '#22C55E'; // Green
    case 'rejected':
      return '#DC143C'; // Crimson
    case 'cancelled':
      return '#DC143C'; // Crimson
    case 'refunded':
      return '#8B5CF6'; // Purple
    default:
      return '#6B7280'; // Gray
  }
};

const TransactionPage = () => {
  const { t } = useTranslation(['transactions', 'common']);
  const dispatch = useDispatch();
  const location = useLocation();
  const { transactions, loading, error, pagination, filters } = useSelector(state => state.transaction);
  
  // Transaction types with translations
  const TRANSACTION_TYPES = [
    { value: 'all', label: t('transactions:filters.allTypes') },
    { value: 'topup', label: t('transactions:filters.topup') },
    { value: 'withdraw', label: t('transactions:filters.withdraw') },
    { value: 'refund', label: t('transactions:filters.refund') },
    { value: 'business', label: t('transactions:filters.business') },
    { value: 'income', label: t('transactions:filters.income') },
    { value: 'transfer', label: t('transactions:filters.transfer') },
    { value: 'bonus', label: t('transactions:filters.bonus') },
    { value: 'debit', label: t('transactions:filters.debit') },
    { value: 'invoice', label: t('transactions:filters.invoice') },
  ];

  const STATUS_OPTIONS = [
    { value: '', label: t('transactions:filters.allStatuses') },
    { value: 'pending', label: t('transactions:filters.pending') },
    { value: 'processing', label: t('transactions:filters.processing') },
    { value: 'completed', label: t('transactions:filters.completed') },
    { value: 'rejected', label: t('transactions:filters.rejected') },
    { value: 'cancelled', label: t('transactions:filters.cancelled') },
    { value: 'refunded', label: t('transactions:filters.refunded') },
  ];
  
  // Get transaction type from URL path
  const getTransactionTypeFromPath = () => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    return lastSegment === 'transactions' ? 'all' : lastSegment;
  };
  
  const [searchTerm, setSearchTerm] = useState(filters.search);
  const [selectedType, setSelectedType] = useState(getTransactionTypeFromPath());
  const [selectedStatus, setSelectedStatus] = useState(filters.status);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(filters.payment_method || '');
  
  // Pagination state - using 0-based page for frontend, 1-based for backend
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTransaction, setViewTransaction] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptTransaction, setReceiptTransaction] = useState(null);
  
  // Bulk operations state
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);

  // Calculate total pages from pagination data
  const totalPages = Math.ceil(pagination.total_count / rowsPerPage) || 1;

  // Update selected type when URL changes
  useEffect(() => {
    const newType = getTransactionTypeFromPath();
    setSelectedType(newType);
    dispatch(setFilters({ type: newType }));
    setPage(0);
  }, [location.pathname, dispatch]);

  // Load transactions on component mount and when filters change
  useEffect(() => {
    loadTransactions();
    // Clear selections when filters change
    setSelectedTransactions([]);
    setBulkStatus('');
  }, [page, rowsPerPage, selectedType, selectedStatus, searchTerm, selectedPaymentMethod]);

  const loadTransactions = useCallback(() => {
    const params = {
      page: page + 1, // backend expects 1-based
      limit: rowsPerPage,
      type: selectedType,
      status: selectedStatus,
      search: searchTerm,
      payment_method: selectedPaymentMethod,
    };
    
    dispatch(fetchTransactions(params));
  }, [dispatch, page, rowsPerPage, selectedType, selectedStatus, searchTerm, selectedPaymentMethod]);

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const handleSearch = () => {
    dispatch(setFilters({ search: searchTerm }));
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    dispatch(setFilters({ search: '' }));
    setPage(0);
  };

  const handleTypeChange = (event) => {
    const value = event.target.value;
    setSelectedType(value);
    dispatch(setFilters({ type: value }));
    setPage(0);
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setSelectedStatus(value);
    dispatch(setFilters({ status: value }));
    setPage(0);
  };

  const handlePaymentMethodChange = (event) => {
    const value = event.target.value;
    setSelectedPaymentMethod(value);
    dispatch(setFilters({ payment_method: value }));
    setPage(0);
  };

  const handleUpdateStatus = (transaction) => {
    setSelectedTransaction(transaction);
    setNewStatus(transaction.status);
    setUpdateDialogOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (selectedTransaction && newStatus) {
      try {
        await dispatch(updateTransaction({
          transactionId: selectedTransaction.id,
          status: newStatus
        })).unwrap();
        
        setSnackbarMessage(t('transactions:messages.statusUpdatedSuccess'));
        setSnackbarOpen(true);
        setUpdateDialogOpen(false);
        loadTransactions(); // Refresh the list
      } catch (error) {
        setSnackbarMessage(t('transactions:messages.failedToUpdateStatus'));
        setSnackbarOpen(true);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialogOpen(false);
    setSelectedTransaction(null);
    setNewStatus('');
  };

  const handleViewTransaction = (transaction) => {
    setViewTransaction(transaction);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewTransaction(null);
  };

  const handleViewReceipt = (transaction) => {
    setReceiptTransaction(transaction);
    setReceiptDialogOpen(true);
  };

  const handleCloseReceiptDialog = () => {
    setReceiptDialogOpen(false);
    setReceiptTransaction(null);
  };

  // Bulk operations handlers
  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTransactions.length === transactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(transactions.map(t => t.id));
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedTransactions.length === 0 || !bulkStatus) {
      setSnackbarMessage(t('transactions:messages.pleaseSelectAndChooseStatus'));
      setSnackbarOpen(true);
      return;
    }

    setBulkUpdateLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Update each selected transaction
      for (const transactionId of selectedTransactions) {
        try {
          await dispatch(updateTransaction({
            transactionId: transactionId,
            status: bulkStatus
          })).unwrap();
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to update transaction ${transactionId}:`, error);
        }
      }

      // Show result message
      if (successCount > 0 && errorCount === 0) {
        setSnackbarMessage(t('transactions:messages.successfullyUpdated', { count: successCount }));
      } else if (successCount > 0 && errorCount > 0) {
        setSnackbarMessage(t('transactions:messages.updatedWithErrors', { success: successCount, error: errorCount }));
      } else {
        setSnackbarMessage(t('transactions:messages.failedToUpdate', { count: errorCount }));
      }
      
      setSnackbarOpen(true);
      
      // Clear selections and refresh
      setSelectedTransactions([]);
      setBulkStatus('');
      loadTransactions();
      
    } catch (error) {
      setSnackbarMessage(t('transactions:messages.bulkUpdateFailed'));
      setSnackbarOpen(true);
    } finally {
      setBulkUpdateLoading(false);
    }
  };

  // Add serial numbers to transactions for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { 
      field: 'select', 
      headerName: '', 
      width: 60, 
      sortable: false, 
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <input
          type="checkbox"
          checked={selectedTransactions.length === transactions.length && transactions.length > 0}
          onChange={handleSelectAll}
          style={{ transform: 'scale(1.2)' }}
        />
      ),
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={selectedTransactions.includes(params.row.id)}
          onChange={() => handleSelectTransaction(params.row.id)}
          style={{ transform: 'scale(1.2)' }}
        />
      ),
    },
    { field: 'sn', headerName: t('transactions:table.sn'), width: 80, sortable: false, filterable: false },
    {
      field: 'transaction_id',
      headerName: t('transactions:table.transactionId'),
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'user_name',
      headerName: t('transactions:table.user'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'type',
      headerName: t('transactions:table.type'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            textTransform: 'capitalize',
            backgroundColor: '#E3F2FD',
            color: '#1976D2',
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: t('transactions:table.status'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            textTransform: 'capitalize',
            backgroundColor: `${getStatusColor(params.value)}20`,
            color: getStatusColor(params.value),
            fontWeight: 'bold',
          }}
        />
      ),
    },
    {
      field: 'amount',
      headerName: t('transactions:table.amount'),
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          ₦{Number(params.value).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'payment_method',
      headerName: t('transactions:table.paymentMethod'),
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'contact',
      headerName: t('transactions:table.contact'),
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.user_phone || params.row.user_email || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: t('transactions:table.date'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'receipt_url',
      headerName: t('transactions:table.receipt'),
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {params.value ? (
            <Tooltip title={t('transactions:tooltips.viewReceipt')}>
              <IconButton
                size="small"
                onClick={() => handleViewReceipt(params.row)}
                sx={{ 
                  color: '#22C55E',
                  '&:hover': {
                    backgroundColor: 'rgba(34, 197, 94, 0.1)'
                  }
                }}
              >
                <Receipt />
              </IconButton>
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ color: '#6B7280', fontStyle: 'italic' }}>
              {t('transactions:table.noReceipt')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: t('transactions:table.actions'),
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('transactions:tooltips.viewDetails')}>
            <IconButton
              size="small"
              onClick={() => handleViewTransaction(params.row)}
              sx={{ 
                color: '#1976D2',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.1)'
                }
              }}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('transactions:tooltips.updateStatus')}>
            <IconButton
              size="small"
              onClick={() => handleUpdateStatus(params.row)}
              sx={{ 
                color: '#DC143C',
                '&:hover': {
                  backgroundColor: 'rgba(220, 20, 60, 0.1)'
                }
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];


  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {selectedType === 'all' ? t('transactions:transactionManagement') : `${t(`transactions:types.${selectedType}`)} ${t('transactions:title')}`}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadTransactions}
          disabled={loading}
          sx={{ borderColor: '#DC143C', color: '#DC143C', minWidth: 'auto' }}
        >
          {t('transactions:refresh')}
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('transactions:filters.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="small" onClick={handleSearch}>
                        <Search />
                      </IconButton>
                      <IconButton size="small" onClick={handleClearSearch}>
                        <Clear />
                      </IconButton>
                    </Box>
                  ),
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('transactions:filters.type')}</InputLabel>
                <Select
                  value={selectedType}
                  onChange={handleTypeChange}
                  label={t('transactions:filters.type')}
                >
                  {TRANSACTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('transactions:filters.status')}</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  label={t('transactions:filters.status')}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('transactions:filters.paymentMethod')}</InputLabel>
                <Select
                  value={selectedPaymentMethod}
                  onChange={handlePaymentMethodChange}
                  label={t('transactions:filters.paymentMethod')}
                >
                  <MenuItem value="">{t('transactions:filters.allMethods')}</MenuItem>
                  <MenuItem value="bank transfer">{t('transactions:filters.bankTransfer')}</MenuItem>
                  <MenuItem value="paystack online">{t('transactions:filters.paystack')}</MenuItem>
                  <MenuItem value="wallet">{t('transactions:filters.wallet')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedTransactions.length > 0 && (
        <Card sx={{ mb: 3, backgroundColor: '#FFF0F3', border: '1px solid #DC143C' }}>
          <CardContent>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center', 
              flexWrap: 'wrap'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                {t('transactions:bulkActions.title', { count: selectedTransactions.length })}
              </Typography>
              
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                <InputLabel>{t('transactions:bulkActions.updateStatusTo')}</InputLabel>
                <Select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  label={t('transactions:bulkActions.updateStatusTo')}
                >
                  {STATUS_OPTIONS.filter(status => status.value !== '').map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleBulkStatusUpdate}
                disabled={!bulkStatus || bulkUpdateLoading}
                sx={{ 
                  backgroundColor: '#DC143C', 
                  '&:hover': { backgroundColor: '#B91C3C' },
                  minWidth: { xs: '100%', sm: 150 }
                }}
              >
                {bulkUpdateLoading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                    {t('transactions:bulkActions.updating')}
                  </>
                ) : (
                  t('transactions:bulkActions.updateSelected')
                )}
              </Button>

              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedTransactions([]);
                  setBulkStatus('');
                }}
                sx={{ borderColor: '#DC143C', color: '#DC143C', minWidth: { xs: '100%', sm: 'auto' } }}
              >
                {t('transactions:bulkActions.clearSelection')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Data Grid */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            <DataGrid
              rows={addSerialNumbers(transactions, page, rowsPerPage)}
              columns={columns}
              loading={loading}
              disableSelectionOnClick
              hideFooterPagination
              hideFooter
              sx={{
                border: 'none',
                minWidth: 1200, // Ensure minimum width for all columns
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme => theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 4px', sm: '8px 12px' }
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('transactions:pagination.page', { current: totalPages === 0 ? 0 : page + 1, total: totalPages })}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('transactions:pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('transactions:pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('transactions:pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Update Status Dialog */}
      <Dialog open={updateDialogOpen} onClose={handleCloseUpdateDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {t('transactions:dialogs.updateStatus.title')}
        </DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('transactions:dialogs.updateStatus.transactionId')} <strong>{selectedTransaction.transaction_id}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('transactions:dialogs.updateStatus.currentStatus')} <strong>{selectedTransaction.status}</strong>
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>{t('transactions:dialogs.updateStatus.newStatus')}</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  label={t('transactions:dialogs.updateStatus.newStatus')}
                >
                  {STATUS_OPTIONS.filter(status => status.value !== '').map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog} color="inherit">
            {t('transactions:dialogs.updateStatus.cancel')}
          </Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
            disabled={!newStatus || newStatus === selectedTransaction?.status}
          >
            {t('transactions:dialogs.updateStatus.updateStatus')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {t('transactions:dialogs.viewDetails.title')}
        </DialogTitle>
        <DialogContent>
          {viewTransaction && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 1 }}>
                    {t('transactions:dialogs.viewDetails.transactionInformation')}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.transactionId')}</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                      {viewTransaction.transaction_id}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.type')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1, textTransform: 'capitalize' }}>
                      {viewTransaction.type}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.status')}</Typography>
                    <Chip
                      label={viewTransaction.status}
                      size="small"
                      sx={{
                        textTransform: 'capitalize',
                        backgroundColor: `${getStatusColor(viewTransaction.status)}20`,
                        color: getStatusColor(viewTransaction.status),
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.amount')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      ₦{Number(viewTransaction.amount).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.paymentMethod')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {viewTransaction.payment_method || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 1 }}>
                    {t('transactions:dialogs.viewDetails.userInformation')}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.username')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {viewTransaction.user_name || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.phone')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {viewTransaction.user_phone || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.email')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {viewTransaction.user_email || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 1 }}>
                    {t('transactions:dialogs.viewDetails.additionalDetails')}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.subject')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-word' }}>
                      {viewTransaction.subject || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.operator')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {viewTransaction.operator_username || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.operatorIp')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace' }}>
                      {viewTransaction.operator_ip || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.createdAt')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {new Date(viewTransaction.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.viewDetails.updatedAt')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {new Date(viewTransaction.updated_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} variant="contained" sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}>
            {t('transactions:dialogs.viewDetails.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Viewer Dialog */}
      <Dialog
        open={receiptDialogOpen}
        onClose={handleCloseReceiptDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#F8F9FA',
          borderBottom: '1px solid #E0E0E0'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
            {t('transactions:dialogs.receipt.title')}
          </Typography>
          <IconButton onClick={handleCloseReceiptDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {receiptTransaction && (
            <Box>
              {/* Transaction Info */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#F8F9FA', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 1 }}>
                  {t('transactions:dialogs.receipt.transactionDetails')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.receipt.transactionId')}</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {receiptTransaction.transaction_id}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.receipt.amount')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#22C55E' }}>
                      ₦{Number(receiptTransaction.amount).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.receipt.user')}</Typography>
                    <Typography variant="body2">
                      {receiptTransaction.user_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('transactions:dialogs.receipt.date')}</Typography>
                    <Typography variant="body2">
                      {new Date(receiptTransaction.created_at).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Receipt Image */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {t('transactions:dialogs.receipt.paymentReceipt')}
                </Typography>
                {receiptTransaction.receipt_url ? (
                  <Box sx={{ 
                    border: '2px dashed #E0E0E0', 
                    borderRadius: 2, 
                    p: 2,
                    backgroundColor: '#FAFAFA'
                  }}>
                    <img
                      src={receiptTransaction.receipt_url}
                      alt="Payment Receipt"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '500px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <Box sx={{ 
                      display: 'none', 
                      p: 3, 
                      textAlign: 'center',
                      color: '#6B7280'
                    }}>
                      <Receipt sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="body2">
                        {t('transactions:dialogs.receipt.failedToLoad')}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    border: '2px dashed #E0E0E0', 
                    borderRadius: 2, 
                    p: 4,
                    backgroundColor: '#FAFAFA',
                    textAlign: 'center'
                  }}>
                    <Receipt sx={{ fontSize: 48, color: '#6B7280', mb: 2 }} />
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      {t('transactions:dialogs.receipt.noReceiptUploaded')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#F8F9FA', borderTop: '1px solid #E0E0E0' }}>
          <Button 
            onClick={handleCloseReceiptDialog} 
            variant="contained" 
            sx={{ 
              backgroundColor: '#DC143C', 
              '&:hover': { backgroundColor: '#B91C3C' } 
            }}
          >
            {t('transactions:dialogs.receipt.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => dispatch(clearError())}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => dispatch(clearError())} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionPage;
